/**
 * Agent 记忆管理适配器
 * 专门处理 AI Agent 相关的记忆，包括性能跟踪、使用偏好、协作模式等
 */

import { MemoryClient } from '../memory-client'
import type {
  MemoryConfig,
  AgentMemory,
  AgentPerformance,
  PerformanceMetrics,
  CollaborationPattern,
  AgentFeedback,
  AgentUsage,
  AgentRecommendation,
  SearchResult
} from '../types'

/**
 * Agent 接口（简化版）
 */
interface Agent {
  id: string
  name: string
  type: 'developer' | 'reviewer' | 'writer' | 'architect' | 'tester' | 'custom'
  capabilities: string[]
  specialties?: string[]
  model?: string
}

/**
 * 任务上下文
 */
interface TaskContext {
  taskType: string
  complexity?: 'low' | 'medium' | 'high'
  techStack?: string[]
  requirements?: string[]
  constraints?: string[]
}

/**
 * Agent 记忆适配器
 */
export class AgentMemoryAdapter {
  private memory: MemoryClient
  private userId: string
  private projectId?: string

  constructor(config: MemoryConfig) {
    this.memory = new MemoryClient(config)
    this.userId = config.userId || 'default'
    this.projectId = config.projectId
  }

  /**
   * 记录 Agent 使用情况
   */
  async recordUsage(usage: AgentUsage): Promise<void> {
    const { agentId, taskType, performance, userSatisfaction, context } = usage

    // 构建使用记录描述
    const usageDescription = [
      `Agent: ${agentId}`,
      `任务类型: ${taskType}`,
      `速度: ${performance.speed}`,
      `质量: ${performance.quality}`,
      `准确度: ${performance.accuracy}`,
      `用户满意度: ${userSatisfaction}/5`,
      context ? `上下文: ${JSON.stringify(context)}` : ''
    ].filter(Boolean).join('\n')

    // 存储使用记录
    await this.memory.add(usageDescription, {
      category: 'agent_usage',
      tags: [
        agentId,
        taskType,
        `speed:${performance.speed}`,
        `quality:${performance.quality}`,
        `satisfaction:${userSatisfaction}`
      ],
      importance: this.calculateUsageImportance(performance, userSatisfaction)
    })

    // 更新 Agent 性能统计
    await this.updateAgentPerformance(agentId, taskType, performance, userSatisfaction)
  }

  /**
   * 推荐合适的 Agent
   */
  async recommendAgents(
    context: TaskContext,
    limit: number = 3
  ): Promise<AgentRecommendation[]> {
    // 构建搜索查询
    const query = this.buildContextQuery(context)
    
    // 搜索相关的 Agent 使用记录
    const results = await this.memory.search(query, {
      filter: {
        category: 'agent_usage',
        tags: context.techStack
      },
      limit: 20
    })

    // 分析并排序 Agent
    const agentScores = this.analyzeAgentPerformance(results, context)
    
    // 获取每个 Agent 的详细性能数据
    const recommendations = await Promise.all(
      agentScores.slice(0, limit).map(async ({ agentId, score, reason }) => {
        const performance = await this.getAgentPerformance(agentId, context.taskType)
        return {
          agentId,
          score,
          reason,
          historicalPerformance: performance
        }
      })
    )

    return recommendations
  }

  /**
   * 学习 Agent 协作模式
   */
  async learnCollaborationPattern(params: {
    agents: string[]
    taskType: string
    outcome: 'success' | 'failure' | 'partial'
    pattern: 'sequential' | 'parallel' | 'hierarchical'
    context?: Record<string, any>
  }): Promise<void> {
    const { agents, taskType, outcome, pattern, context } = params

    // 构建协作模式描述
    const patternDescription = [
      `协作模式: ${pattern}`,
      `Agents: ${agents.join(' -> ')}`,
      `任务类型: ${taskType}`,
      `结果: ${outcome}`,
      context ? `上下文: ${JSON.stringify(context)}` : ''
    ].filter(Boolean).join('\n')

    // 存储协作模式
    await this.memory.add(patternDescription, {
      category: 'agent_usage',
      tags: [
        ...agents,
        taskType,
        `pattern:${pattern}`,
        `outcome:${outcome}`,
        'collaboration'
      ],
      importance: outcome === 'success' ? 0.9 : 0.3
    })
  }

  /**
   * 获取推荐的协作模式
   */
  async getCollaborationPatterns(
    taskType: string,
    availableAgents: string[]
  ): Promise<CollaborationPattern[]> {
    // 搜索相关的协作模式
    const results = await this.memory.search(
      `协作模式 ${taskType} ${availableAgents.join(' ')}`,
      {
        filter: {
          category: 'agent_usage',
          tags: ['collaboration']
        },
        limit: 10
      }
    )

    // 解析并返回协作模式
    return this.parseCollaborationPatterns(results, availableAgents)
  }

  /**
   * 记录用户反馈
   */
  async recordFeedback(feedback: AgentFeedback & { agentId: string }): Promise<void> {
    const { agentId, taskId, rating, comment, timestamp } = feedback

    await this.memory.add(
      `Agent ${agentId} 反馈: 评分 ${rating}/5` +
      (comment ? `, 评论: ${comment}` : '') +
      `, 任务: ${taskId}`,
      {
        category: 'feedback',
        tags: [agentId, 'agent_feedback', `rating:${rating}`],
        importance: rating >= 4 ? 0.8 : rating <= 2 ? 0.9 : 0.5
      }
    )
  }

  /**
   * 获取 Agent 性能统计
   */
  async getAgentPerformance(
    agentId: string,
    taskType?: string
  ): Promise<PerformanceMetrics | null> {
    // 搜索 Agent 的使用记录
    const searchQuery = taskType 
      ? `Agent: ${agentId} 任务类型: ${taskType}`
      : `Agent: ${agentId}`

    const results = await this.memory.search(searchQuery, {
      filter: {
        category: 'agent_usage',
        tags: [agentId]
      },
      limit: 100
    })

    if (results.length === 0) return null

    // 计算性能指标
    return this.calculatePerformanceMetrics(results)
  }

  /**
   * 获取 Agent 专长
   */
  async getAgentSpecialties(agentId: string): Promise<string[]> {
    // 获取 Agent 的所有高评分任务
    const results = await this.memory.search(`Agent: ${agentId}`, {
      filter: {
        category: 'agent_usage',
        tags: [agentId, 'quality:high']
      },
      limit: 50
    })

    // 分析任务类型，找出专长
    const taskTypes: Record<string, number> = {}
    
    results.forEach(({ memory }) => {
      const taskType = this.extractTaskType(memory.content)
      if (taskType) {
        taskTypes[taskType] = (taskTypes[taskType] || 0) + 1
      }
    })

    // 返回出现频率最高的任务类型
    return Object.entries(taskTypes)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([type]) => type)
  }

  /**
   * 获取 Agent 对比分析
   */
  async compareAgents(
    agentIds: string[],
    taskType?: string
  ): Promise<Record<string, {
    performance: PerformanceMetrics | null
    specialties: string[]
    pros: string[]
    cons: string[]
  }>> {
    const comparison: Record<string, any> = {}

    for (const agentId of agentIds) {
      const performance = await this.getAgentPerformance(agentId, taskType)
      const specialties = await this.getAgentSpecialties(agentId)
      const analysis = await this.analyzeAgentStrengthsWeaknesses(agentId)

      comparison[agentId] = {
        performance,
        specialties,
        ...analysis
      }
    }

    return comparison
  }

  /**
   * 获取 Agent 使用趋势
   */
  async getUsageTrends(
    agentId: string,
    days: number = 30
  ): Promise<{
    daily: Record<string, number>
    weeklyAverage: number
    trend: 'increasing' | 'stable' | 'decreasing'
  }> {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const results = await this.memory.search(`Agent: ${agentId}`, {
      filter: {
        category: 'agent_usage',
        tags: [agentId],
        timeRange: {
          start: startDate,
          end: new Date()
        }
      },
      limit: 1000
    })

    return this.calculateUsageTrends(results, days)
  }

  // ============= 私有方法 =============

  /**
   * 更新 Agent 性能统计
   */
  private async updateAgentPerformance(
    agentId: string,
    taskType: string,
    performance: AgentUsage['performance'],
    satisfaction: number
  ): Promise<void> {
    // 构建性能更新记录
    const performanceUpdate = [
      `Agent ${agentId} 性能更新`,
      `任务类型: ${taskType}`,
      `速度: ${performance.speed}`,
      `质量: ${performance.quality}`,
      `准确度: ${performance.accuracy}`,
      `满意度: ${satisfaction}`,
      `时间: ${new Date().toISOString()}`
    ].join('\n')

    await this.memory.add(performanceUpdate, {
      category: 'agent_usage',
      tags: [
        agentId,
        taskType,
        'performance_update'
      ],
      importance: 0.7
    })
  }

  /**
   * 计算使用重要性
   */
  private calculateUsageImportance(
    performance: AgentUsage['performance'],
    satisfaction: number
  ): number {
    let importance = 0.5

    // 根据性能调整
    const speedScore = { fast: 0.1, normal: 0, slow: -0.1 }
    const qualityScore = { high: 0.2, medium: 0.1, low: -0.1 }
    
    importance += speedScore[performance.speed] || 0
    importance += qualityScore[performance.quality] || 0
    importance += performance.accuracy * 0.2
    importance += (satisfaction / 5) * 0.2

    return Math.max(0, Math.min(1, importance))
  }

  /**
   * 构建上下文查询
   */
  private buildContextQuery(context: TaskContext): string {
    const parts = [
      `任务类型: ${context.taskType}`,
      context.complexity ? `复杂度: ${context.complexity}` : '',
      context.techStack?.length ? `技术栈: ${context.techStack.join(' ')}` : '',
      context.requirements?.join(' ') || ''
    ].filter(Boolean)

    return parts.join(' ')
  }

  /**
   * 分析 Agent 性能
   */
  private analyzeAgentPerformance(
    results: SearchResult[],
    context: TaskContext
  ): Array<{ agentId: string; score: number; reason: string }> {
    const agentScores: Record<string, {
      totalScore: number
      count: number
      reasons: Set<string>
    }> = {}

    results.forEach(({ memory, score }) => {
      const agentId = this.extractAgentId(memory.content)
      if (!agentId) return

      if (!agentScores[agentId]) {
        agentScores[agentId] = {
          totalScore: 0,
          count: 0,
          reasons: new Set()
        }
      }

      agentScores[agentId].totalScore += score
      agentScores[agentId].count++

      // 分析性能原因
      if (memory.content.includes('质量: high')) {
        agentScores[agentId].reasons.add('历史表现优秀')
      }
      if (memory.content.includes(`任务类型: ${context.taskType}`)) {
        agentScores[agentId].reasons.add('擅长此类任务')
      }
    })

    // 计算平均分并排序
    return Object.entries(agentScores)
      .map(([agentId, data]) => ({
        agentId,
        score: data.totalScore / data.count,
        reason: Array.from(data.reasons).join(', ') || '有使用经验'
      }))
      .sort((a, b) => b.score - a.score)
  }

  /**
   * 解析协作模式
   */
  private parseCollaborationPatterns(
    results: SearchResult[],
    availableAgents: string[]
  ): CollaborationPattern[] {
    const patterns: Record<string, CollaborationPattern> = {}

    results.forEach(({ memory }) => {
      const content = memory.content
      const agents = this.extractAgentList(content)
      const pattern = this.extractPattern(content)
      const outcome = this.extractOutcome(content)
      const taskType = this.extractTaskType(content)

      if (!agents.length || !pattern || !taskType) return

      // 检查是否所有 Agent 都可用
      const allAvailable = agents.every(agent => 
        availableAgents.includes(agent)
      )
      if (!allAvailable) return

      const key = `${agents.join('-')}-${pattern}`
      
      if (!patterns[key]) {
        patterns[key] = {
          agents,
          taskType,
          pattern,
          successRate: 0,
          usageCount: 0
        }
      }

      patterns[key].usageCount++
      if (outcome === 'success') {
        patterns[key].successRate++
      }
    })

    // 计算成功率并返回
    return Object.values(patterns)
      .map(pattern => ({
        ...pattern,
        successRate: pattern.successRate / pattern.usageCount
      }))
      .sort((a, b) => b.successRate - a.successRate)
  }

  /**
   * 计算性能指标
   */
  private calculatePerformanceMetrics(
    results: SearchResult[]
  ): PerformanceMetrics {
    let totalCount = 0
    let successCount = 0
    let totalTime = 0
    let totalRating = 0
    let ratingCount = 0

    results.forEach(({ memory }) => {
      totalCount++
      
      // 提取性能数据
      if (memory.content.includes('质量: high')) {
        successCount++
      }
      
      const rating = this.extractRating(memory.content)
      if (rating) {
        totalRating += rating
        ratingCount++
      }
      
      // 简化时间计算
      totalTime += 1 // 假设每次使用1单位时间
    })

    return {
      count: totalCount,
      successRate: totalCount > 0 ? successCount / totalCount : 0,
      averageTime: totalCount > 0 ? totalTime / totalCount : 0,
      userRating: ratingCount > 0 ? totalRating / ratingCount : 0,
      lastUsed: new Date()
    }
  }

  /**
   * 分析 Agent 优缺点
   */
  private async analyzeAgentStrengthsWeaknesses(
    agentId: string
  ): Promise<{ pros: string[]; cons: string[] }> {
    const pros: Set<string> = new Set()
    const cons: Set<string> = new Set()

    // 获取高评分记录
    const highRated = await this.memory.search(`Agent: ${agentId}`, {
      filter: {
        category: 'agent_usage',
        tags: [agentId, 'quality:high']
      },
      limit: 20
    })

    // 获取低评分记录
    const lowRated = await this.memory.search(`Agent: ${agentId}`, {
      filter: {
        category: 'agent_usage',
        tags: [agentId, 'quality:low']
      },
      limit: 20
    })

    // 分析优点
    highRated.forEach(({ memory }) => {
      if (memory.content.includes('速度: fast')) {
        pros.add('响应速度快')
      }
      if (memory.content.includes('准确度: 0.9') || memory.content.includes('准确度: 1')) {
        pros.add('准确度高')
      }
    })

    // 分析缺点
    lowRated.forEach(({ memory }) => {
      if (memory.content.includes('速度: slow')) {
        cons.add('响应速度慢')
      }
      if (memory.content.includes('质量: low')) {
        cons.add('输出质量不稳定')
      }
    })

    return {
      pros: Array.from(pros),
      cons: Array.from(cons)
    }
  }

  /**
   * 计算使用趋势
   */
  private calculateUsageTrends(
    results: SearchResult[],
    days: number
  ): {
    daily: Record<string, number>
    weeklyAverage: number
    trend: 'increasing' | 'stable' | 'decreasing'
  } {
    const daily: Record<string, number> = {}
    
    // 统计每日使用量
    results.forEach(({ memory }) => {
      const date = new Date(memory.createdAt).toISOString().split('T')[0]
      daily[date] = (daily[date] || 0) + 1
    })

    // 计算周平均
    const totalUsage = Object.values(daily).reduce((sum, count) => sum + count, 0)
    const weeklyAverage = (totalUsage / days) * 7

    // 分析趋势
    const dates = Object.keys(daily).sort()
    const firstWeek = dates.slice(0, 7).reduce((sum, date) => sum + (daily[date] || 0), 0)
    const lastWeek = dates.slice(-7).reduce((sum, date) => sum + (daily[date] || 0), 0)
    
    let trend: 'increasing' | 'stable' | 'decreasing' = 'stable'
    if (lastWeek > firstWeek * 1.2) trend = 'increasing'
    else if (lastWeek < firstWeek * 0.8) trend = 'decreasing'

    return { daily, weeklyAverage, trend }
  }

  // ============= 提取工具方法 =============

  private extractAgentId(content: string): string | null {
    const match = content.match(/Agent: (\S+)/)
    return match ? match[1] : null
  }

  private extractTaskType(content: string): string | null {
    const match = content.match(/任务类型: (\S+)/)
    return match ? match[1] : null
  }

  private extractPattern(content: string): CollaborationPattern['pattern'] | null {
    const match = content.match(/协作模式: (\S+)/)
    return match ? match[1] as CollaborationPattern['pattern'] : null
  }

  private extractOutcome(content: string): string | null {
    const match = content.match(/结果: (\S+)/)
    return match ? match[1] : null
  }

  private extractAgentList(content: string): string[] {
    const match = content.match(/Agents: (.+)/)
    return match ? match[1].split(' -> ').map(s => s.trim()) : []
  }

  private extractRating(content: string): number | null {
    const match = content.match(/满意度: (\d+)\/5/)
    return match ? parseInt(match[1]) : null
  }
}